import { useQuery } from "@tanstack/react-query";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { getMe } from "../api/auth";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../style";

// Queries Algolia directly: calls with the crts_index key on /me, meetings with
// the secured meetings_search key (null when meetings search isn't set up).

// Our own markers rather than <em>, so highlights render as React elements and
// the indexed text is never treated as HTML
const PRE = "\uE000";
const POST = "\uE001";

const clients = new Map();
function client(appId, key) {
  const id = `${appId}:${key}`;
  if (!clients.has(id)) clients.set(id, algoliasearch(appId, key));
  return clients.get(id);
}

async function query(access, params) {
  const { results } = await client(access.app_id, access.key).searchForHits({
    requests: [
      {
        indexName: access.index,
        highlightPreTag: PRE,
        highlightPostTag: POST,
        snippetEllipsisText: "…",
        ...params,
      },
    ],
  });
  return results[0];
}

// One hit per sqid, in case an index splits records
const unique = (hits) => {
  const seen = new Set();
  return hits.filter((h) => !seen.has(h.sqid) && seen.add(h.sqid));
};

const matched = (r) => r && r.matchLevel && r.matchLevel !== "none";

function firstSnippet(hit, attributes) {
  for (const a of attributes) {
    if (matched(hit._snippetResult?.[a])) return hit._snippetResult[a].value;
  }
  return null;
}

async function meetingsQuery(access, text, hitsPerPage) {
  const page = await query(access, {
    query: text,
    hitsPerPage,
    attributesToRetrieve: [
      "sqid",
      "title",
      "recorded_timestamp",
      "duration_seconds",
    ],
    attributesToHighlight: ["title"],
    attributesToSnippet: [
      "transcript:24",
      "summary:24",
      "notes:24",
      "user_notes:24",
    ],
  });
  return {
    total: page.nbHits ?? page.hits.length,
    hits: unique(page.hits).map((hit) => ({
      sqid: hit.sqid,
      title: matched(hit._highlightResult?.title)
        ? hit._highlightResult.title.value
        : hit.title || "Untitled meeting",
      recordedAt: hit.recorded_timestamp
        ? new Date(hit.recorded_timestamp * 1000).toISOString()
        : null,
      durationSeconds: hit.duration_seconds,
      snippet: firstSnippet(hit, [
        "transcript",
        "summary",
        "notes",
        "user_notes",
      ]),
    })),
  };
}

async function callsQuery(access, text, hitsPerPage) {
  const page = await query(access, {
    query: text,
    hitsPerPage,
    attributesToRetrieve: [
      "sqid",
      "tel_number",
      "user_name",
      "user_ext",
      "direction",
      "start_timestamp",
      "effective_timezone",
    ],
    attributesToHighlight: [],
    attributesToSnippet: ["transcription:20", "summary:20"],
  });
  return {
    total: page.nbHits ?? page.hits.length,
    hits: unique(page.hits).map((hit) => ({
      sqid: hit.sqid,
      title: [hit.tel_number, hit.user_name || hit.user_ext]
        .filter(Boolean)
        .join(" · "),
      recordedAt: hit.start_timestamp
        ? new Date(hit.start_timestamp * 1000).toISOString()
        : null,
      // Shown in the call's zone, as on the call page
      effectiveTimezone: hit.effective_timezone,
      snippet: firstSnippet(hit, ["transcription", "summary"]),
    })),
  };
}

const meetingsAccess = (user) => user?.meetings_search ?? null;
const callsAccess = (user) =>
  user?.sidekick_configured && user?.search_id && user?.search_key
    ? { app_id: user.search_id, key: user.search_key, index: "crts_index" }
    : null;

// Whether /me gives the search dialog anything to search
export const canSearch = (user) =>
  !!(meetingsAccess(user) || callsAccess(user));

// Secured keys expire, so on a 403 refresh /me (and with it the key) once
function useSearch(
  kind,
  pick,
  run,
  text,
  { hitsPerPage = 20, enabled = true },
) {
  const { apiUser, setApiUser } = useAuth();
  const access = pick(apiUser);
  const trimmed = text.trim();

  const search = async () => {
    try {
      return await run(access, trimmed, hitsPerPage);
    } catch (e) {
      if (e?.status !== 403) throw e;
      const user = (await getMe()).data.data;
      setApiUser(user);
      const fresh = pick(user);
      if (!fresh) throw e;
      return run(fresh, trimmed, hitsPerPage);
    }
  };

  const result = useQuery(["search", kind, trimmed, hitsPerPage], search, {
    enabled: enabled && !!access && trimmed !== "",
    keepPreviousData: true,
    staleTime: 30000,
    retry: false,
  });
  return { ...result, available: !!access };
}

export const useMeetingSearch = (text, options = {}) =>
  useSearch("meetings", meetingsAccess, meetingsQuery, text, options);

export const useCallSearch = (text, options = {}) =>
  useSearch("calls", callsAccess, callsQuery, text, options);

const MARK = {
  background: "rgba(88, 185, 183, 0.3)",
  color: colors.text,
  borderRadius: "3px",
  padding: "0 2px",
};

// Renders a highlighted Algolia value, matches as <mark>
export function Highlighted({ value, markStyle = MARK }) {
  if (!value) return null;
  const parts = [];
  let rest = value;
  let i = 0;
  while (rest) {
    const start = rest.indexOf(PRE);
    if (start === -1) {
      parts.push(rest);
      break;
    }
    if (start > 0) parts.push(rest.slice(0, start));
    const end = rest.indexOf(POST, start);
    const hit = rest.slice(start + 1, end === -1 ? undefined : end);
    parts.push(
      <mark key={i++} style={markStyle}>
        {hit}
      </mark>,
    );
    rest = end === -1 ? "" : rest.slice(end + 1);
  }
  return <>{parts}</>;
}
